/**
 * Audio helpers for the Gemini Live browser integration.
 *
 * Two concerns live here:
 *   1. Encoding 24 kHz mono PCM (what Gemini sends back) into a WAV Blob
 *      that <audio> can play directly via URL.createObjectURL.
 *   2. Capturing the browser's microphone (typically 48 kHz Float32) and
 *      downsampling to 16 kHz Int16 PCM (what Gemini expects on the wire).
 *
 * Both pieces are pure-ish DOM glue, kept out of LiveSession.ts so that file
 * stays under the ~250-line smell threshold.
 */

/** Base64-decode a string into a Uint8Array (browser-safe — no Buffer). */
export const base64ToBytes = (b64: string): Uint8Array => {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

/** Base64-encode a Uint8Array (browser-safe — no Buffer). */
export const bytesToBase64 = (bytes: Uint8Array): string => {
  // Chunked to avoid blowing the call-stack on large buffers.
  const CHUNK = 0x8000
  let bin = ''
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(bin)
}

/**
 * Wrap raw signed 16-bit little-endian mono PCM in a minimal WAV container.
 * Used to play a complete Gemini speaking turn back through <audio>.
 */
export const pcmToWavBlob = (
  pcmBytes: Uint8Array,
  sampleRate: number,
  channels: number,
): Blob => {
  const bitsPerSample = 16
  const byteRate = sampleRate * channels * (bitsPerSample / 8)
  const blockAlign = channels * (bitsPerSample / 8)
  const dataSize = pcmBytes.byteLength
  const buffer = new ArrayBuffer(44 + dataSize)
  const view = new DataView(buffer)

  const writeStr = (offset: number, s: string): void => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i))
  }

  writeStr(0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeStr(8, 'WAVE')
  writeStr(12, 'fmt ')
  view.setUint32(16, 16, true)              // PCM fmt chunk size
  view.setUint16(20, 1, true)               // format = PCM
  view.setUint16(22, channels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitsPerSample, true)
  writeStr(36, 'data')
  view.setUint32(40, dataSize, true)
  new Uint8Array(buffer, 44).set(pcmBytes)

  return new Blob([buffer], { type: 'audio/wav' })
}

/**
 * Downsample a Float32 sample buffer from `sourceRate` to `targetRate`
 * by block-averaging. Returns a signed 16-bit little-endian PCM Uint8Array.
 *
 * Browser audio is typically 48 kHz Float32; Gemini Live expects 16 kHz Int16.
 * Block-averaging is crude but sufficient for voice (the SDK's example uses
 * the same approach).
 */
export const downsampleFloat32ToPcm16 = (
  input: Float32Array,
  sourceRate: number,
  targetRate: number,
): Uint8Array => {
  if (targetRate === sourceRate) return floatToPcm16(input)
  const ratio = sourceRate / targetRate
  const outLength = Math.floor(input.length / ratio)
  const out = new Int16Array(outLength)
  for (let i = 0; i < outLength; i++) {
    const start = Math.floor(i * ratio)
    const end = Math.floor((i + 1) * ratio)
    let sum = 0
    let count = 0
    for (let j = start; j < end && j < input.length; j++) {
      sum += input[j] ?? 0
      count++
    }
    const avg = count > 0 ? sum / count : 0
    const clamped = Math.max(-1, Math.min(1, avg))
    out[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff
  }
  return new Uint8Array(out.buffer, out.byteOffset, out.byteLength)
}

const floatToPcm16 = (input: Float32Array): Uint8Array => {
  const out = new Int16Array(input.length)
  for (let i = 0; i < input.length; i++) {
    const v = input[i] ?? 0
    const clamped = Math.max(-1, Math.min(1, v))
    out[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff
  }
  return new Uint8Array(out.buffer, out.byteOffset, out.byteLength)
}
